/**
 * Cart Test Customer Sync
 *
 * Keeps the hidden `_CLP3` line-item property (TEST_CUSTOMER_PROPERTY, see
 * assets/utilities.js) in sync on every merchandise line in the cart:
 * "isTestCustomer" when customer.metafields.user_types.is_test_customer is
 * true, "" (empty) otherwise — identical on every line.
 *
 * Deliberately independent from assets/cart-vift-connect-sync.js (_CLP2):
 * this value depends only on the ma:customer meta tag, resolved synchronously
 * with no portal lookup or cart-item metafield involved, so it has no reason
 * to share a reconcile pass (and therefore no reason to ever be delayed or
 * skipped by a portal fetch hanging or a VIFT Connect write failing).
 *
 * Runs once on load and again on every `cart:update`. A correction (any line
 * whose _CLP3 doesn't match the desired value) is applied one line at a time
 * via /cart/change.js — a property write changes the line's key, so each
 * write re-picks its target from the freshest cart response, same as
 * cart-hkg-district.js's #writeLineStamps. Writes are serialized through a
 * shared queue so overlapping triggers never race each other's line keys.
 */

import { fetchConfig, TEST_CUSTOMER_PROPERTY } from "@theme/utilities";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { isBcrsFeeLine, visibleItemCount } from "@theme/bcrs-fee";

/** `sourceId` of the re-render this module dispatches, so its own listener skips it. */
const STAMP_SOURCE_ID = "cart-test-customer-sync";

/** Value stamped when the customer is a test customer; disabled is the empty string. */
const ENABLED_VALUE = "isTestCustomer";

/** Serializes writes so overlapping triggers never race the same line keys. */
let syncQueue = Promise.resolve();

/**
 * @param {boolean} desired
 * @returns {string}
 */
function testCustomerValue(desired) {
  return desired ? ENABLED_VALUE : "";
}

/**
 * @param {any} item A cart line item.
 * @param {boolean} desired
 * @returns {boolean}
 */
function lineNeedsStamp(item, desired) {
  if (isBcrsFeeLine(item)) return false;
  const properties = item.properties ?? {};
  // A missing key is NOT equivalent to an empty-string value here — every
  // non-fee line must carry an explicit _CLP3 (even "") once this module has
  // run, so consumers can rely on the key always being present.
  if (!Object.prototype.hasOwnProperty.call(properties, TEST_CUSTOMER_PROPERTY)) return true;
  return properties[TEST_CUSTOMER_PROPERTY] !== testCustomerValue(desired);
}

/** Whether the current customer has user_types.is_test_customer set, from the ma:customer meta tag. */
function isTestCustomer() {
  return document.querySelector('meta[name="ma:customer"]')?.getAttribute("data-is-test-customer") === "true";
}

/** Section ids of every cart surface on the page, so a correction re-renders them in the same round trip. */
function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * Applies `desired` to every merchandise line that doesn't already carry it,
 * one /cart/change.js call at a time (a property write changes the line key).
 * @param {boolean} desired
 */
async function reconcile(desired) {
  let cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: "application/json" } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  if (!cart || !Array.isArray(cart.items)) return;

  const sectionIds = collectCartSectionIds();
  let changed = false;

  for (
    let attempts = cart.items.filter((/** @type {any} */ it) => lineNeedsStamp(it, desired)).length;
    attempts > 0;
    attempts--
  ) {
    const item = cart.items.find((/** @type {any} */ it) => lineNeedsStamp(it, desired));
    if (!item) break;

    const properties = { ...item.properties, [TEST_CUSTOMER_PROPERTY]: testCustomerValue(desired) };
    const remaining = cart.items.filter((/** @type {any} */ it) => lineNeedsStamp(it, desired)).length;

    /** @type {Record<string, unknown>} */
    const body = { id: item.key, quantity: item.quantity, properties };
    if (remaining === 1 && sectionIds.length) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }

    const updated = await fetch(Theme.routes.cart_change_url, fetchConfig("json", { body: JSON.stringify(body) }))
      .then((r) => r.json())
      .catch(() => null);
    if (!updated || updated.status || !Array.isArray(updated.items)) {
      console.error(
        "[cart-test-customer-sync] Failed to update line property:",
        updated?.description ?? "unknown error"
      );
      break;
    }
    cart = updated;
    changed = true;
  }

  if (!changed) return;

  const autoOpenDrawers = Array.from(document.querySelectorAll("cart-drawer-component[auto-open]"));
  for (const drawer of autoOpenDrawers) drawer.removeAttribute("auto-open");

  document.dispatchEvent(
    new CartUpdateEvent(cart, STAMP_SOURCE_ID, {
      source: STAMP_SOURCE_ID,
      itemCount: visibleItemCount(cart),
      sections: cart.sections ?? undefined,
    })
  );

  setTimeout(() => {
    for (const drawer of autoOpenDrawers) drawer.setAttribute("auto-open", "");
  }, 0);
}

/**
 * @param {(Event & { detail?: { sourceId?: string } }) | undefined} [event]
 */
function sync(event) {
  if (event?.detail?.sourceId === STAMP_SOURCE_ID) return;

  syncQueue = syncQueue
    .then(() => reconcile(isTestCustomer()))
    .catch((err) => console.error("[cart-test-customer-sync] Sync failed:", err));
}

document.addEventListener(ThemeEvents.cartUpdate, sync);
sync();
